import { toast } from 'react-toastify'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useNavigate, useParams } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import to from 'await-to-js'
import { handleFormError } from 'utils/handleFormError'
import { TextInput } from 'components/FormUtils/TextInput'
import Select from 'components/FormUtils/Select'
import { PhoneInput } from 'components/FormUtils/PhoneInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { z } from 'zod'
import {
  extensionZodValidator,
  phoneNumberZodValidor,
} from 'utils/zodValidators'
import { AddressTextInput } from './MobileCompanyCreate'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getIdWithPrefix } from 'utils/pathUtils'
import { ContactCompanyType } from 'models/contact_companies'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { useQueryClient } from '@tanstack/react-query'

// Domain regex for validation
const domainRegex =
  /^(((?!-))(xn--|_)?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/

// Zod schema for mobile company form
const mobileCompanyFormSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  domain: z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (v) => {
        if (!v) return true
        return domainRegex.test(v) && v.includes('.')
      },
      { message: 'Enter domain as: domain.com' }
    ),
  category: z.number({
    required_error: 'Please select a category',
    invalid_type_error: 'Please select a category',
  }),
  sub_category: z.number({
    required_error: 'Please select a type',
    invalid_type_error: 'Please select a type',
  }),
  address: z.string().optional().nullable(),
  phone_number: phoneNumberZodValidor,
  extension: extensionZodValidator,
  distributors: z.array(z.number()).optional(),
  taglist: z.array(z.number()).optional(),
  notes: z.string().optional().nullable(),
})

type MobileCompanyFormValues = z.infer<typeof mobileCompanyFormSchema>

export default function MobileCompanyEdit() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const api = apiService()
  const queryClient = useQueryClient()

  const companyID = urlParams?.company_id
    ? getIdWithPrefix(
        urlParams.company_id,
        preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]
      )
    : undefined

  const { data: companyData, isLoading } = api.useGetCompany(companyID || 0)
  const { data: categories } = api.useGetCompanyCategories()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()

  const categoryOptions = useMemo(
    () =>
      categories?.map((category) => ({
        label: category.name,
        value: category.id,
      })) || [],
    [categories]
  )

  const defaultValues = useMemo<Partial<MobileCompanyFormValues>>(() => {
    if (!companyData) return {}
    return {
      name: companyData.name || '',
      domain: companyData.domain || '',
      category: companyData.category_id,
      sub_category: companyData.sub_category_id,
      address: companyData.address || '',
      phone_number: companyData.phone_number || '',
      extension: companyData.extension || '',
      distributors: companyData.distributors?.map((d) => d.id) || [],
      taglist: companyData.taglist?.map((t) => t.id) || [],
      notes: companyData.note?.notes || '',
    }
  }, [companyData])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    watch,
  } = useForm<MobileCompanyFormValues>({
    mode: 'onChange',
    resolver: zodResolver(mobileCompanyFormSchema),
    defaultValues,
  })

  const watchCategory = watch('category')

  const typeOptions = useMemo(() => {
    if (!watchCategory || !categories) return []
    const categoryObj = categories.find((c) => c.id === watchCategory)
    if (!categoryObj) return []
    return categoryObj.sub_categories
      .map((subCategory) => ({
        label: subCategory.name,
        value: subCategory.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [watchCategory, categories])

  const handleClose = () => {
    navigate(-1)
  }

  const handleSubmitSuccess = () => {
    toast.success('Company updated successfully')
    // Invalidate the company query to refresh data
    if (companyID) {
      queryClient.invalidateQueries({ queryKey: ['company', companyID] })
    }
    navigate(-1)
  }

  const submitApiAction = async (values: MobileCompanyFormValues) => {
    const payload = {
      ...values,
      country: companyData?.country || 'US',
      state: companyData?.state || '',
      city: companyData?.city || '',
      domain: values.domain?.toLowerCase() || '',
      name: values.name?.trim() || '',
      address: values.address || '',
      zipcode: companyData?.zipcode || '',
    }
    return api.updateCompany(companyID!, payload)
  }

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(submitApiAction(values))
      if (err) {
        console.warn(err)
        handleFormError(err, setError)
        return
      }
      handleSubmitSuccess()
    },
    (err) => console.warn(err)
  )

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  return (
    <MobileModal
      handleBack={() => navigate(-1)}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Edit company
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Edit Company Details
          </h1>
          <div className="text-xs text-gray-500 mt-2">
            Modify information about this company.
          </div>
        </div>
      }
      footer={
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </FbButton>
          </div>
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="primary"
              onClick={onSubmit}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </FbButton>
          </div>
        </div>
      }
    >
      <div className="px-2 space-y-4">
        <TextInput
          control={control}
          name="name"
          label="Company"
          placeholder="Start typing company name"
        />

        <TextInput
          control={control}
          name="domain"
          label="Company Domain"
          placeholder="https://www.company.com"
          optional
        />

        <div className="pt-2">
          <div className="text-base font-medium text-gray-800 mb-1">
            Target Categorization
          </div>
          <div className="space-y-2">
            <Select
              control={control}
              name="category"
              placeholder="Category"
              options={categoryOptions}
              className="mt-0 mb-2.5"
            />
            <Select
              control={control}
              name="sub_category"
              placeholder="Type"
              options={typeOptions}
              disabled={!watchCategory}
              className="mt-0"
            />
          </div>
        </div>

        <AddressTextInput control={control} name="address" />

        <PhoneInput
          control={control}
          name="phone_number"
          extensionName="extension"
          label="Phone Number"
          optional
        />

        <Select
          control={control}
          name="distributors"
          label="Account Management"
          placeholder="Known Distributors"
          options={distributorsOptions}
          isMulti
          optional
        />

        <Select
          control={control}
          name="taglist"
          label=""
          placeholder="Tag(s)"
          options={tagsOptions}
          isMulti
          optional
        />

        <TextareaInput
          control={control}
          name="notes"
          label="Notes"
          placeholder="Write additional notes here"
          optional
        />
      </div>
    </MobileModal>
  )
}
